/**
 * dashboardConfig.js
 *
 * Centralized dashboard configuration for all roles
 * Defines what sections each role should see and the display layout
 *
 * Reference: DASHBOARD_MODULAR_ARCHITECTURE_PLAN.md Phase 7, Task 7.1
 */

/**
 * Dashboard configuration object
 * Maps each role to its specific dashboard configuration
 *
 * Each role config contains:
 * - title: Dashboard title to display
 * - description: Brief description of dashboard purpose
 * - sections: Array of section names to display in order
 * - layout: Layout type ('activity-focused', 'overview', 'device-focused', 'team-management', 'financial')
 * - features: Available features for the role
 */
export const DASHBOARD_CONFIG = {
  // Technician - Activity-focused dashboard
  technician: {
    title: 'Technician Dashboard',
    description: 'Activity-focused dashboard for maintenance engineers',
    sections: [
      'overdue-alerts',
      'work-queue',
      'maintenance-due',
      'recent-activity',
      'stats'
    ],
    layout: 'activity-focused',
    features: {
      canViewFinancial: false,
      canAssignTasks: false,
      canManageTeam: false,
      canManageCompliance: false,
      scope: 'department'
    },
    description_detailed: 'View assigned complaints, maintenance schedule, and activities. Department-scoped with no financial data.',
    icon: '👨‍🔧'
  },

  // SuperAdmin - Full system overview
  superadmin: {
    title: 'SuperAdmin Dashboard',
    description: 'System-wide overview with all analytics and financial data',
    sections: [
      'system-stats',
      'critical-alerts',
      'financial-overview',
      'critical-complaints',
      'maintenance-overview',
      'recent-activity',
      'compliance'
    ],
    layout: 'overview',
    features: {
      canViewFinancial: true,
      canAssignTasks: true,
      canManageTeam: true,
      canManageCompliance: true,
      scope: 'organization'
    },
    description_detailed: 'System-wide overview with access to all data, financial metrics, and compliance status.',
    icon: '🔑'
  },

  // Admin - Location-scoped overview
  admin: {
    title: 'Admin Dashboard',
    description: 'Location-scoped overview with financial data',
    sections: [
      'location-stats',
      'maintenance-alerts',
      'financial-overview',
      'device-breakdown',
      'complaints-summary',
      'recent-activity'
    ],
    layout: 'overview',
    features: {
      canViewFinancial: true,
      canAssignTasks: true,
      canManageTeam: true,
      canManageCompliance: true,
      scope: 'location'
    },
    description_detailed: 'Location-scoped overview with financial data for all assigned locations.',
    icon: '👔'
  },

  // Manager - Team management focus
  manager: {
    title: 'Manager Dashboard',
    description: 'Team management with workload and performance metrics',
    sections: [
      'team-stats',
      'alert-summary',
      'team-workload',
      'all-complaints',
      'maintenance-schedule',
      'engineer-performance',
      'recent-activity'
    ],
    layout: 'team-management',
    features: {
      canViewFinancial: true,  // Manager can view financial data (budget oversight)
      canAssignTasks: true,
      canManageTeam: true,
      canManageCompliance: false,
      scope: 'organization'
    },
    description_detailed: 'Team workload management with all complaints, maintenance view, and financial data for budget oversight.',
    icon: '👨‍💼'
  },

  // Coordinator - Department financial overview
  coordinator: {
    title: 'Coordinator Dashboard',
    description: 'Department-scoped overview with financial data',
    sections: [
      'department-stats',
      'financial-summary',
      'device-status',
      'department-alerts',
      'department-complaints',
      'maintenance-overview',
      'recent-activity'
    ],
    layout: 'financial',
    features: {
      canViewFinancial: true,
      canAssignTasks: true,
      canManageTeam: false,
      canManageCompliance: true,
      scope: 'department'
    },
    description_detailed: 'Department-scoped overview with full financial visibility and compliance metrics.',
    icon: '💼'
  },

  // Custodian - Device-focused
  custodian: {
    title: 'Custodian Dashboard',
    description: 'Device inventory and compliance management',
    sections: [
      'inventory-stats',
      'device-status',
      'maintenance-alerts',
      'maintenance-due',
      'device-compliance',
      'recent-activity'
    ],
    layout: 'device-focused',
    features: {
      canViewFinancial: false,
      canAssignTasks: false,
      canManageTeam: false,
      canManageCompliance: true,
      scope: 'department'
    },
    description_detailed: 'Device inventory management with compliance tracking and maintenance scheduling.',
    icon: '📋'
  },

  // Central Help Desk - Minimal location-scoped view
  central_help_desk: {
    title: 'Helpdesk Dashboard',
    description: 'Location overview with basic information',
    sections: [
      'quick-stats',
      'location-alerts',
      'device-summary',
      'complaint-overview',
      'recent-activity'
    ],
    layout: 'overview',
    features: {
      canViewFinancial: false,
      canAssignTasks: false,
      canManageTeam: false,
      canManageCompliance: false,
      scope: 'location'
    },
    description_detailed: 'Location-scoped view with device status, complaints, and alerts. No financial data.',
    icon: '📞'
  }
};

/**
 * Get configuration for a specific role
 * @param {string} role - User role
 * @returns {Object} Dashboard configuration for the role
 */
export const getDashboardConfig = (role) => {
  return DASHBOARD_CONFIG[role];
};

/**
 * Get all available roles
 * @returns {Array} Array of role names
 */
export const getAllRoles = () => {
  return Object.keys(DASHBOARD_CONFIG);
};

/**
 * Check if a role has a specific feature
 * @param {string} role - User role
 * @param {string} feature - Feature key (e.g., 'canViewFinancial')
 * @returns {boolean} True if role has the feature enabled
 */
export const hasFeature = (role, feature) => {
  const config = getDashboardConfig(role);
  return config.features?.[feature] || false;
};

/**
 * Get scope for a role
 * @param {string} role - User role
 * @returns {string} Scope type ('organization', 'location', or 'department')
 */
export const getScope = (role) => {
  const config = getDashboardConfig(role);
  return config.features?.scope || 'location';
};

/**
 * Role descriptions for UI
 * Maps each role to a human-readable description
 */
export const ROLE_DESCRIPTIONS = {
  technician: 'Biomedical Engineer - Maintains and repairs medical devices',
  superadmin: 'SuperAdmin - Full system access and control',
  admin: 'Administrator - Manages locations and operations',
  manager: 'Manager - Oversees engineering team and workload',
  coordinator: 'Coordinator - Manages department operations',
  custodian: 'Custodian - Manages device inventory and compliance',
  central_help_desk: 'Central Help Desk - Handles location-specific requests and inquiries'
};

/**
 * Role hierarchy for permission checks
 * Higher numbers = higher privilege level
 */
export const ROLE_HIERARCHY = {
  central_help_desk: 0,
  technician: 1,
  custodian: 2,
  coordinator: 3,
  manager: 4,
  admin: 5,
  superadmin: 6
};

/**
 * Check if one role has higher privilege than another
 * @param {string} role1 - First role
 * @param {string} role2 - Second role to compare against
 * @returns {boolean} True if role1 has higher or equal privilege than role2
 */
export const hasHigherPrivilege = (role1, role2) => {
  return (ROLE_HIERARCHY[role1] || 0) >= (ROLE_HIERARCHY[role2] || 0);
};

/**
 * Default export for backward compatibility
 */
export default DASHBOARD_CONFIG;
